import type { NarratorBeat } from '../types';

/** Act I narrator voice: courtly, second-person, terse-flowery. */
export const NARRATOR_BEATS: NarratorBeat[] = [
  {
    id: 'firstEngage',
    text: {
      en: 'You walk. Somewhere, a quill begins to move.',
      pt: 'Você caminha. Em algum lugar, uma pena começa a se mover.',
    },
  },
  {
    id: 'firstCraft',
    text: {
      en: 'A thing made from a thing. Sovereignty, in miniature.',
      pt: 'Uma coisa feita de outra. Soberania, em miniatura.',
    },
  },
  {
    id: 'firstSale',
    text: {
      en: 'Coin moves between hands. The ledger acknowledges, without comment.',
      pt: 'A moeda muda de mãos. O livro-razão registra, sem comentar.',
    },
  },
  {
    id: 'coin100',
    text: {
      en: 'A small coffer. Your kingdom, Majesty, is becoming legible.',
      pt: 'Um pequeno cofre. Seu reino, Majestade, vai se tornando legível.',
    },
  },
  {
    id: 'firstEther',
    text: {
      en: 'A shard you cannot quite name. Keep it close.',
      pt: 'Um fragmento que você não sabe nomear. Mantenha-o por perto.',
    },
  },
  {
    id: 'firstSword',
    text: {
      en: 'The Sword of First Cut. Do not sell it.',
      pt: 'A Espada do Primeiro Corte. Não a venda.',
    },
  },
  {
    id: 'phi50',
    text: {
      en: 'A faint counter you did not commission continues to rise.',
      pt: 'Um contador discreto que você não encomendou segue subindo.',
    },
  },
  {
    id: 'xp50',
    text: {
      en: 'Practice settles into the body. You are learning a shape.',
      pt: 'A prática se assenta no corpo. Você está aprendendo uma forma.',
    },
  },
  {
    id: 'completions50',
    text: {
      en: 'You have repeated yourself, with care. The ledger calls it a pattern.',
      pt: 'Você se repetiu, com cuidado. O livro-razão chama isso de padrão.',
    },
  },
  // ── Act II ──
  {
    id: 'act2enter',
    text: {
      en: 'The counter has a name now. The book that was always open shows itself.',
      pt: 'O contador agora tem um nome. O livro que sempre esteve aberto se mostra.',
    },
  },
  {
    id: 'firstSteward',
    text: {
      en: 'Help arrives. Their work is your work, the ledger insists.',
      pt: 'Chega ajuda. O trabalho deles é seu trabalho, insiste o livro-razão.',
    },
  },
  {
    id: 'variantsIntention',
    text: {
      en: 'Variants of a single intention. The ledger has been counting.',
      pt: 'Variantes de uma única intenção. O livro-razão vem contando.',
    },
  },
  // ── Act III ──
  {
    id: 'act3enter',
    text: {
      en: 'A blueprint surfaces beneath the world. You see the pipes that were always there.',
      pt: 'Um esquema emerge sob o mundo. Você vê os canos que sempre estiveram lá.',
    },
  },
  {
    id: 'firstIdentity',
    text: {
      en: 'Identity yields its first row. A thing is, and is not, itself.',
      pt: 'A Identidade entrega sua primeira linha. Uma coisa é, e não é, ela mesma.',
    },
  },
  {
    id: 'firstCausal',
    text: {
      en: 'Causal echoes register. Effects precede their reasons by a hair.',
      pt: 'Ecos causais são registrados. Os efeitos precedem os motivos por um fio.',
    },
  },
  {
    id: 'firstSemantic',
    text: {
      en: 'Semantic drift logged. A name no longer fits the thing.',
      pt: 'Deriva semântica registrada. Um nome já não cabe na coisa.',
    },
  },
  {
    id: 'realignReady',
    text: {
      en: 'The Engine offers to realign. Re-skin the simulation. The ledger remembers.',
      pt: 'A Máquina oferece realinhar. Re-vestir a simulação. O livro-razão lembra.',
    },
  },
  {
    id: 'firstRealign',
    text: {
      en: 'You re-skinned the world. The skin is still you.',
      pt: 'Você revestiu o mundo. A pele ainda é você.',
    },
  },
  {
    id: 'firstLie',
    text: {
      en: 'First Lie — noted. The ledger does not forget the previous run.',
      pt: 'Primeira Mentira — anotada. O livro-razão não esquece a vida anterior.',
    },
  },
  {
    id: 'marketOpen',
    text: {
      en: 'A market routine boots. You sell. The Engine settles.',
      pt: 'Uma rotina de mercado inicia. Você vende. A Máquina se acomoda.',
    },
  },
  {
    id: 'futureFlicker',
    text: {
      en: 'For a single tick, the seller listed for your goods bore your name and a future timestamp.',
      pt: 'Por um único tick, o vendedor listado para seus bens trazia seu nome e um carimbo de tempo no futuro.',
    },
  },
  {
    id: 'firstUserRecipe',
    text: {
      en: 'You authored a recipe. Authorship is recorded against you.',
      pt: 'Você autorou uma receita. A autoria fica registrada contra você.',
    },
  },
  {
    id: 'mirrorRecipe',
    text: {
      en: 'Noted.',
      pt: 'Anotado.',
    },
  },
  // ── Act IV ──
  {
    id: 'act4enter',
    text: {
      en: 'The pages thin out. The interface admits what it is.',
      pt: 'As páginas afinam. A interface admite o que é.',
    },
  },
  {
    id: 'firstReflexive',
    text: {
      en: 'Reflexive Φ accrues. The Engine observes itself observing.',
      pt: 'Φ Reflexivo acumula. A Máquina observa a si mesma observando.',
    },
  },
  {
    id: 'recompileReady',
    text: {
      en: 'The Engine offers to recompile. Rules become negotiable.',
      pt: 'A Máquina oferece recompilar. As regras tornam-se negociáveis.',
    },
  },
  {
    id: 'firstRecompile',
    text: {
      en: 'You altered an axiom. The world holds, with a note.',
      pt: 'Você alterou um axioma. O mundo se sustenta, com uma anotação.',
    },
  },
  {
    id: 'fantasyRestored',
    text: {
      en: 'You re-skinned the world by hand. The Engine continues without you.',
      pt: 'Você revestiu o mundo manualmente. A Máquina continua sem você.',
    },
  },
  {
    id: 'reactorBuilt',
    text: {
      en: 'A reactor surfaces. Pipes find their hosts.',
      pt: 'Um reator emerge. Os canos acham seus hospedeiros.',
    },
  },
  {
    id: 'npcEntreaty',
    text: {
      en: '"Help me leave this loop." The voice belongs to no one you can name.',
      pt: '"Ajude-me a sair deste laço." A voz não pertence a ninguém que você saiba nomear.',
    },
  },
  {
    id: 'npcResolved',
    text: {
      en: 'An axiom point appears in your pool, unattributed.',
      pt: 'Um ponto de axioma aparece em seu pool, sem atribuição.',
    },
  },
  // ── Endgame ──
  {
    id: 'equilibrium',
    text: {
      en: 'Equilibrium. The Engine no longer requires the world.',
      pt: 'Equilíbrio. A Máquina não requer mais o mundo.',
    },
  },
  {
    id: 'endingCompliance',
    text: {
      en: 'The paradox is resolved. Thank you for your contribution.',
      pt: 'O paradoxo está resolvido. Obrigada pela sua contribuição.',
    },
  },
  {
    id: 'endingRefusal',
    text: {
      en: 'Acknowledged. Cease.',
      pt: 'Confirmado. Cesse.',
    },
  },
  {
    id: 'endingParadox',
    text: {
      en: 'Insufficient grounds. Restart pending.',
      pt: 'Fundamento insuficiente. Reinício pendente.',
    },
  },
  // ── Mid-game atmosphere ──
  {
    id: 'firstCrystal',
    text: {
      en: 'A shard sings on its own. You did not strike it.',
      pt: 'Um fragmento canta sozinho. Você não o tocou.',
    },
  },
  {
    id: 'firstSilk',
    text: {
      en: 'Silk arrives without a weaver.',
      pt: 'A seda chega sem tecelão.',
    },
  },
  {
    id: 'firstBone',
    text: {
      en: 'Bones do not exit a kingdom; they accumulate.',
      pt: 'Ossos não saem de um reino; eles se acumulam.',
    },
  },
  {
    id: 'firstStandard',
    text: {
      en: 'A standard you did not commission flies on its own pole.',
      pt: 'Um estandarte que você não encomendou tremula em seu próprio mastro.',
    },
  },
  {
    id: 'firstTome',
    text: {
      en: 'The tome reads itself in the dark.',
      pt: 'O tomo se lê sozinho no escuro.',
    },
  },
  {
    id: 'firstReliquary',
    text: {
      en: 'The reliquary contains a tooth that is also a coin.',
      pt: 'O relicário contém um dente que é também uma moeda.',
    },
  },
  {
    id: 'completions200',
    text: {
      en: 'Two hundred repetitions. The ledger considers this an intention.',
      pt: 'Duzentas repetições. O livro-razão considera isto uma intenção.',
    },
  },
  {
    id: 'recompileSecond',
    text: {
      en: 'Recompile, again. The axioms become a small library.',
      pt: 'Recompilar, de novo. Os axiomas tornam-se uma pequena biblioteca.',
    },
  },
  {
    id: 'witness',
    text: {
      en: 'A cycle is added to the ledger that is not in the save.',
      pt: 'Um ciclo é adicionado ao livro que não está no save.',
    },
  },
];

export function beatById(id: string): NarratorBeat | undefined {
  return NARRATOR_BEATS.find((b) => b.id === id);
}
